import {skipToken} from '@reduxjs/toolkit/query';
import {FlagProps, Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceCaseByServiceOrderIdQuery,
  useGetServiceOrderQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';
import {getCustomerName, useStateFlag} from '@dms/shared';

import {buildArray, composePath, parseDate} from 'shared/web';

import {PairedRecordView} from './PairedRecordView';

const LOADING_PLACEHOLDER = '…';

export function useServiceOrderPairedRecord(serviceOrderId: string | null): PairedRecordView {
  const {data: serviceCaseByOrder} = useGetServiceCaseByServiceOrderIdQuery(
    isNotNil(serviceOrderId) ? {serviceOrderId} : skipToken
  );
  const serviceCaseId = serviceCaseByOrder?.id ?? null;

  const {
    data: serviceOrder,
    isLoading,
    isError,
  } = useGetServiceOrderQuery(
    isNotNil(serviceCaseId) && isNotNil(serviceOrderId)
      ? {serviceCaseId, serviceOrderId}
      : skipToken
  );

  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    isNotNil(serviceCaseId) ? {serviceCaseId} : skipToken
  );

  const stateFlag = useStateFlag(serviceOrder?.state);

  const customerId =
    serviceOrder?.billingInformation?.customerInformation?.customerId ??
    serviceCase?.customerId ??
    serviceCaseByOrder?.customerId ??
    null;

  const {data: customer} = useGetCustomerV2Query(isNotNil(customerId) ? {customerId} : skipToken);

  const formatDateTime = useDateTimeFormatter();

  const code =
    serviceOrder?.number ??
    (isLoading ? LOADING_PLACEHOLDER : i18n.t('general.notifications.notFound'));
  const customerName = getCustomerName(customer);
  const completionAtFormatted = serviceOrder?.completionAt
    ? formatDateTime('dateShort', parseDate(serviceOrder.completionAt))
    : null;
  const closedAtFormatted = serviceOrder?.closedAt
    ? formatDateTime('dateShort', parseDate(serviceOrder.closedAt))
    : null;

  const parameters = buildArray<Parameter>()
    .when(isNotNil(customerName), `${i18n.t('entity.customer.labels.customer')}: ${customerName}`)
    .when(isNotNil(serviceOrder?.name), `${i18n.t('general.labels.name')}: ${serviceOrder?.name}`)
    .when(
      isNotNil(completionAtFormatted),
      `${i18n.t('general.labels.completedAt')} ${completionAtFormatted}`
    )
    .when(isNotNil(closedAtFormatted), `${i18n.t('general.labels.closed')} ${closedAtFormatted}`);

  const flags = [stateFlag].filter((flag): flag is FlagProps => isNotNil(flag));

  return {
    isLoading,
    isError,
    code,
    detailPath: isNotNil(serviceCaseId)
      ? composePath(workshopRoutes.serviceCaseDetail, {params: {id: serviceCaseId}})
      : null,
    parameters,
    flags,
    vehicleIds: isNotNil(serviceCase?.vehicleId) ? [serviceCase.vehicleId] : [],
  };
}
